import { loader } from 'graphql.macro';

import { getCookie, setCookie } from '../cookies';
import { CartApolloClient } from './client';

import {
  CreateCartMutation,
  CreateCartMutationVariables,
} from './cart-api-queries-mutations/CreateCartMutation.shopify.generated';
import { GetCartQuery, GetCartQueryVariables } from './cart-api-queries-mutations/GetCart.shopify.generated';
import {
  AddCartLinesMutation,
  AddCartLinesMutationVariables,
} from './cart-api-queries-mutations/AddCartLine.shopify.generated';
import {
  UpdateCartLinesMutation,
  UpdateCartLinesMutationVariables,
} from './cart-api-queries-mutations/UpdateCartLine.shopify.generated';
import {
  CartNoteUpdateMutation,
  CartNoteUpdateMutationVariables,
} from './cart-api-queries-mutations/CartNoteUpdate.shopify.generated';
import {
  UpdateCartDiscountCodesMutation,
  UpdateCartDiscountCodesMutationVariables,
} from './cart-api-queries-mutations/UpdateCartDiscount.shopify.generated';
import {
  CartBuyerIdentityUpdateMutation,
  CartBuyerIdentityUpdateMutationVariables,
  CountryCode,
} from 'src/generated/shopify-graphql';
import { Customer } from '../environment';
import { generateAjaxCartCookie, generateStorefrontCookie } from './cart-api-utils';

const extendedCreateCartMutation = loader('./cart-api-queries-mutations/CreateCartMutation.shopify.graphql');
const extendedGetCartQuery = loader('./cart-api-queries-mutations/GetCart.shopify.graphql');
const extendedAddLineCartMutation = loader('./cart-api-queries-mutations/AddCartLine.shopify.graphql');
const extendedUpdateCartLineMutation = loader('./cart-api-queries-mutations/UpdateCartLine.shopify.graphql');
const extendedUpdateCartDiscountMutation = loader('./cart-api-queries-mutations/UpdateCartDiscount.shopify.graphql');
const extendedCartNoteMutation = loader('./cart-api-queries-mutations/CartNoteUpdate.shopify.graphql');
const cartBuyerIdentityMutation = loader('./cart-api-queries-mutations/CartBuyerIdentityUpdate.shopify.graphql');

const SHOPIFY_CART_EXPIRE_DAYS = 30;

// This mutation creates a cart and returns information about the cart.
export const createCart = async () => {
  try {
    const variables = { cartInput: {} };
    const email = Customer?.email;

    variables.cartInput = {
      buyerIdentity: {
        countryCode: window?.Shopify?.country ?? 'US',
        email,
      },
    };
    const createCartResponse = await CartApolloClient.mutate<CreateCartMutation, CreateCartMutationVariables>({
      mutation: extendedCreateCartMutation,
      variables: variables,
    });
    const { id } = createCartResponse.data?.cartCreate?.cart || { id: '' };
    const cartCookie = generateAjaxCartCookie(id);
    setCookie('cart', cartCookie, SHOPIFY_CART_EXPIRE_DAYS);
    return createCartResponse.data?.cartCreate;
  } catch (error) {
    console.log('Cart creation failed.');
  }
  return null;
};

// Query a cart by id and return some of the cart's objects.
export const fetchCart = async () => {
  const cartCookie = getCookie('cart');
  const cartId = generateStorefrontCookie(cartCookie);
  try {
    if (cartId) {
      const variables = {
        cartId,
      };
      const cartResponse = await CartApolloClient.query<GetCartQuery, GetCartQueryVariables>({
        query: extendedGetCartQuery,
        variables: variables,
      });
      const data = cartResponse.data;

      // update buyer identity in-case if there is any mismatch, especially when
      // the user change country by url or when the customer logged in/out.
      const email = Customer?.email;
      const countryCode = window?.Shopify?.country ?? 'US';
      const isChangeBuyerIdentity =
        data.cart?.buyerIdentity?.countryCode !== countryCode || data.cart?.buyerIdentity?.email !== email;
      if (!data) {
        setCookie('cart', cartId, 0);
        const createCartResponse = await createCart();
        return createCartResponse;
      } else if (isChangeBuyerIdentity) {
        const options = {
          countryCode,
          email,
        };
        const buyerIdentityUpdateResponse = await updateCartBuyerIdentity(options);
        return buyerIdentityUpdateResponse;
      }
      return data;
    } else {
      const createCartResponse = await createCart();
      return createCartResponse;
    }
  } catch (error) {
    console.log('Cart fetching failed.');
    return null;
  }
};

// This mutation adds lines to existing cart, returns the quantity and product id.
export const addLineItemToCart = async (items: LineItems) => {
  const cartCookie = getCookie('cart');
  const cartId = generateStorefrontCookie(cartCookie);

  const lines = items.map((item) => ({
    merchandiseId: `gid://shopify/ProductVariant/${item.merchandiseId}`,
    quantity: item.quantity,
    attributes: item.cartItemAttributes ?? [],
  }));
  try {
    const variables = {
      cartId,
      lines,
    };
    const cartResponse = await CartApolloClient.mutate<AddCartLinesMutation, AddCartLinesMutationVariables>({
      mutation: extendedAddLineCartMutation,
      variables: variables,
    });
    return cartResponse.data?.cartLinesAdd;
  } catch (error) {
    console.log('Add line item to cart failed.');
  }
  return null;
};
// This mutation is used to add a product variant of the same type to the cart. In the below example, the quantity
// of the variant (in variables.json) is increased and the id and quantity are returned to confirm they are correct.
export const updateCartLineItem = async (lineIds: string[], quantity: number) => {
  try {
    const cartCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartCookie);
    const lines = lineIds?.map((id) => ({
      id: id || '',
      quantity,
    }));
    const variables = {
      cartId,
      lines,
    };
    const cartResponse = await CartApolloClient.mutate<UpdateCartLinesMutation, UpdateCartLinesMutationVariables>({
      mutation: extendedUpdateCartLineMutation,
      variables: variables,
    });
    return cartResponse.data?.cartLinesUpdate;
  } catch (error) {
    console.log('Update line item to cart API failed.', error);
  }
  return null;
};

// This mutation updates the discount codes applied to a given cart and returns the cart id and
// discountCodes' 'code' and 'applicable' fields
export const applyPromoCodeToCart = async (coupon: string) => {
  try {
    const cartCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartCookie);
    const variables = {
      cartId,
      discountCodes: [coupon],
    };
    const cartResponse = await CartApolloClient.mutate<
      UpdateCartDiscountCodesMutation,
      UpdateCartDiscountCodesMutationVariables
    >({
      mutation: extendedUpdateCartDiscountMutation,
      variables: variables,
    });
    return cartResponse.data?.cartDiscountCodesUpdate;
  } catch (error) {
    console.log('Apply promo code failed.');
  }
};

// Updates cart note, returns cart.
export const cartNoteUpdate = async (note: string) => {
  try {
    const cartCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartCookie);
    const variables = {
      cartId,
      note,
    };
    const cartResponse = await CartApolloClient.mutate<CartNoteUpdateMutation, CartNoteUpdateMutationVariables>({
      mutation: extendedCartNoteMutation,
      variables: variables,
    });
    return cartResponse.data?.cartNoteUpdate;
  } catch (error) {
    console.log('Cart note update failed.');
  }
};

// Updates buyer identity and return cart.
// In-order to remove the buyer identity from cart we need to pass email and customerAccessToken as null
export const updateCartBuyerIdentity = async (options: buyerIdentityOptions) => {
  try {
    const cartCookie = getCookie('cart');
    const cartId = generateStorefrontCookie(cartCookie);
    const variables = {
      buyerIdentity: {
        email: options.email,
        countryCode: options.countryCode as CountryCode,
      },
      cartId,
    };
    const cartResponse = await CartApolloClient.mutate<
      CartBuyerIdentityUpdateMutation,
      CartBuyerIdentityUpdateMutationVariables
    >({
      mutation: cartBuyerIdentityMutation,
      variables: variables,
    });
    return cartResponse.data?.cartBuyerIdentityUpdate;
  } catch (error) {
    console.log('cart Buyer Identity Update failed.');
  }
};

export type LineItem = {
  merchandiseId: number;
  quantity: number;
  cartItemAttributes?: CartItemAttributes[];
};
export type LineItems = LineItem[];

export type VariantIds = Record<string, number>;

export type CartItemProperties = {
  [key: string]: string | number | boolean;
};

export type CartItemAttributes = {
  key: string;
  value: string;
};

export type Credentials = {
  email: string;
  password: string;
};

export interface buyerIdentityOptions {
  countryCode: string;
  customerAccessToken?: string | null;
  email?: string | null;
  phone?: string | null;
}
